// Auth key-exchange failure classification (ENG-8260).
//
// A leaf module — no imports — so both the API client (which throws these) and
// the manager's persistent-session decision path (which reads them) can depend
// on it without a cycle, and so the classifier is cheap to unit-test.
//
// WHY THIS EXISTS
// ---------------
// `/host/exchange` failures used to surface to the manager as a bare `Error`
// carrying only a message string. That left the caller unable to tell
// "the platform API is unwell right now" (HTTP 5xx, socket reset) from
// "this credential is not valid" (401/403, revoked key) — so it treated both
// as an auth verdict and tore down the agent's running tmux session. A ~2s API
// blip on `alyve-host-1` on 2026-07-29 was therefore enough to kill a healthy,
// mid-turn session for `dwight` and cancel its runs. See ENG-8260.
//
// The distinction is the whole fix, so it is modelled in the type system rather
// than recovered by matching on message text.

/**
 * A failure of the `tlk_` host-key → JWT exchange.
 *
 * `transient` is the load-bearing field: true means "retry, this says nothing
 * about the credential", false means "this is a verdict on the credential and
 * callers may act on it".
 *
 * The `message` format is deliberately unchanged from the pre-ENG-8260 bare
 * `Error` so operator-facing manager.log lines and any log greps keep working.
 */
export class ApiKeyExchangeError extends Error {
  constructor(
    message: string,
    /** HTTP status of the failed exchange, or null for a transport-level failure. */
    public readonly status: number | null,
    /**
     * True when the failure carries no information about the credential's
     * validity (5xx, 429, or a transport error) and is therefore worth
     * retrying. False for a genuine auth verdict (401/403/revoked).
     */
    public readonly transient: boolean,
  ) {
    super(message);
    this.name = 'ApiKeyExchangeError';
  }
}

/**
 * Classify an HTTP status from the exchange endpoint as transient or a verdict.
 *
 * - `>= 500` — the API is unwell (500 Internal Server Error, 502/503/504 from a
 *   proxy or a restarting server). Says nothing about the key.
 * - `429` — rate limited. Also says nothing about the key.
 * - everything else (notably 400/401/403/404) — a verdict. 401/403 mean the
 *   credential was rejected; those must keep their current fail-closed
 *   behaviour.
 */
export function isTransientExchangeStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

/**
 * True when an error thrown out of the auth-exchange path is a transient
 * platform fault rather than a verdict on the credential.
 *
 * Deliberately conservative: an error shape we do not recognise returns
 * `false`. The caller's `false` branch is the pre-ENG-8260 fail-closed
 * behaviour (refuse to spawn, stop a running session), so defaulting to
 * `false` means the new session-preserving branch only ever fires on positive
 * evidence of a transient fault — never on the absence of evidence. An
 * unclassifiable auth error must not be able to talk us into keeping a session
 * alive under credentials we cannot vouch for.
 */
export function isTransientAuthFailure(err: unknown): boolean {
  return err instanceof ApiKeyExchangeError && err.transient;
}

/**
 * Short, non-secret tag for the decision log line. Never includes the message
 * body (which carries the API host and an obfuscated key).
 */
export function exchangeFailureKind(err: unknown): string {
  if (!(err instanceof ApiKeyExchangeError)) return 'unclassified';
  const status = err.status === null ? 'transport' : String(err.status);
  return err.transient ? `transient-${status}` : `verdict-${status}`;
}
